import Title from '@cohort/components-xps/components/Title';
import {isCohortError} from '@cohort/shared-frontend/common/errors';
import {useTurnstile} from '@cohort/shared-frontend/hooks/useTurnstile';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import {Input} from '@cohort/wallet/components/forms/Input';
import {DividerWithText} from '@cohort/wallet/components/modals/auth/DividerWithText';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import useNotify from '@cohort/wallet/hooks/notify';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useShopify from '@cohort/wallet/hooks/useShopify';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {loginWithCustomToken} from '@cohort/wallet/lib/Authentication';
import {UserApi} from '@cohort/wallet/lib/Endpoints';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

const RESEND_CODE_DELAY_SEC = 30; // 30 sec

export type EmailVerificationModalProps = {
  email: string;
  merchantSlug: string;
  successCallback?: () => void;
  errorCallback?: () => void;
  handleGoBack: () => void;
  hideEmailChange?: boolean;
};

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  email,
  merchantSlug,
  successCallback,
  errorCallback,
  handleGoBack,
  hideEmailChange,
}) => {
  const setInitEmail = useUserStore(store => store.setInitEmail);
  const {accentColor, hasDarkBg} = useThemeContext();
  const notify = useNotify();
  const {token, resetToken} = useTurnstile();
  const [isLoading, setIsLoading] = useState(false);

  const [timeLeft, setTimeLeft] = useState(RESEND_CODE_DELAY_SEC);

  const [invalidOtpError, setInvalidOtpError] = useState(false);
  const {t} = useTranslation('components', {keyPrefix: 'modals.auth.emailVerificationModal'});
  const shopify = useShopify();

  const verifyOtpCode = useApi(UserApi.verifyOtpCode);
  const sendSigninCode = useApi(UserApi.sendSigninCode);
  const otpCode = useRef('');

  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (timeLeft > 0) {
        setTimeLeft(timeLeft - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  const handleSubmit = useCallback(
    async (event?: React.SyntheticEvent) => {
      event?.preventDefault();
      if (!token) {
        return;
      }
      setIsLoading(true);
      try {
        const customToken = await verifyOtpCode(merchantSlug, email, otpCode.current, token);
        await loginWithCustomToken(customToken);
        if (successCallback) {
          successCallback();
        }
      } catch (err: unknown) {
        if (isCohortError(err, 'auth.invalid-otp-code')) {
          setInvalidOtpError(true);
          return;
        }
        errorCallback?.();
      } finally {
        setIsLoading(false);
        resetToken?.();
      }
    },
    [token, verifyOtpCode, merchantSlug, email, successCallback, errorCallback, resetToken]
  );

  const onHandleChangeEmail = useCallback(() => {
    if (shopify.active) {
      shopify.signInRedirect();
    } else {
      setInitEmail(undefined);
      handleGoBack();
    }
  }, [shopify, handleGoBack, setInitEmail]);

  const onHandleResendCode = useCallback(async () => {
    if (!token) {
      return;
    }
    setTimeLeft(RESEND_CODE_DELAY_SEC);
    try {
      await sendSigninCode(email, merchantSlug, token);
    } catch (err: unknown) {
      notify('error', err);
    } finally {
      resetToken?.();
    }
  }, [token, sendSigninCode, email, merchantSlug, notify, resetToken]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setInvalidOtpError(false);
    otpCode.current = event.target.value;

    if (otpCode.current.length === 6) {
      void handleSubmit();
    }
  };

  return (
    <div className="space-y-5 px-4 pb-10 md:px-0 md:pb-0">
      <div className="flex h-full flex-col items-center md:space-y-1">
        <Title
          testId="otp-verification-code-title"
          content={t('codeTitle')}
          classes="text-center md:text-left"
        />
        <Subtitle
          testId="otp-verification-code-subtitle"
          content={t('codeContent', {email})}
          classes="text-center md:text-left"
        />
      </div>
      <form onSubmit={handleSubmit} ref={formRef}>
        <label
          htmlFor="email"
          className={classNames(
            'block text-sm font-medium',
            hasDarkBg ? 'text-white/70' : 'text-black/70'
          )}
        >
          {t('codePlaceholder')}
        </label>
        <div className="mt-1 h-full flex-col space-x-0 space-y-3 md:flex md:space-x-0">
          <Input
            testId="otp"
            type="text"
            name="otp"
            accentColor={accentColor}
            placeholder="------"
            hasDarkBg={hasDarkBg}
            maxLength={6}
            autoFocus
            onChange={handleChange}
            required
            classes="tracking-superwide"
          />

          <PrimaryButton
            testId="continue"
            type="submit"
            isLoading
            text={t('signinContinue')}
            className="w-full"
            disabled={otpCode.current.length !== 6 || isLoading}
            tracking={{
              namespace: 'login.emailVerificationModal.continue',
            }}
          />
        </div>
        {invalidOtpError && (
          <p className="mt-2 text-sm text-red-600" id="email-error">
            {t('codeInvalid')}
          </p>
        )}
      </form>

      <DividerWithText text={t('codeIssueTitle')} />
      <label
        htmlFor="email"
        className={classNames(
          'block text-sm font-medium',
          hasDarkBg ? 'text-white/70' : 'text-black/70'
        )}
      >
        {t('codeIssueContent')}
      </label>

      <div className="mt-1 h-full flex-col space-x-0 space-y-3 md:flex md:space-x-0">
        {!hideEmailChange && (
          <SecondaryButton
            testId="change-email"
            text={t('changeEmail', {email})}
            onClick={onHandleChangeEmail}
            className="w-full"
            tracking={{
              namespace: 'login.emailVerificationModal.changeEmail',
            }}
          />
        )}

        <SecondaryButton
          testId="resend"
          text={timeLeft > 0 ? t('codeResendTimer', {numSeconds: timeLeft}) : t('codeResend')}
          onClick={onHandleResendCode}
          className="w-full"
          disabled={timeLeft > 0}
          tracking={{
            namespace: 'login.emailVerificationModal.resendCode',
          }}
        />
      </div>
    </div>
  );
};

export default EmailVerificationModal;
